<template>
  <div class="detail-section" :class="{ 'loading-size': isLoading }">
    <div class="m-2 text-center" v-if="isLoading">
      <font-awesome-icon
        :icon="['fa', 'circle-notch']"
        class="fa-spin m-auto spinner fa-5x egx-partner-color"
      />
    </div>
    <template>
      <ValidationObserver
        tag="form"
        @submit.prevent=""
        ref="prqSave"
        class="detail-section-form"
        v-show="!isLoading"
      >
        <NoConfiguredPRQs v-if="!fetchedData.length && !isLoading" :stepConfiguration="stepConfiguration"/>
        <div v-for="section in fetchedData" :key="section.id" v-else>
          <prq :section="section" @answerList="addToAnswerList" v-if="section.question_type === 'Question' && isPrq"/>
          <attachment :section="section" @answerList="addToAnswerList" v-if="section.question_type === 'Attachment' && isAttachment"/>
        </div>
        <step-button
          :steps-response="stepsResponse"
          @navigate="navigate"
          @save-data="saveAnswer"
          :data-save-required="true"
          v-if="!isLoading"
          :display-save-changes-btn="isPrq && query.page !== 'tc'"
          :displayNextStepBtn="!isSingleMode"
        />
      </ValidationObserver>
    </template>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import Prq from './Prqs.vue';
import Attachment from './Attachment.vue';
import GroupQuestion from '../../configurations/settings/group-questions.json';
import StepButton from '@/components/project/stepButton.vue';
import redirectPath from '@/shared/mixins/redirect-path';
import NoConfiguredPRQs from './NoConfiguredPRQs.vue';
import projectMixins from '@/shared/mixins/projectMixins';

export default {
  name: 'DetailSection',

  components: {
    StepButton,
    Multiselect,
    Prq,
    Attachment,
    NoConfiguredPRQs,
  },

  mixins: [redirectPath, projectMixins],

  props: {
    content: Object,
    stepsResponse: Object,
    navigate: Function,
    projectId: String,
    projectStatus: String,
    stepConfiguration: Object,
    fulfillSyncDetails: Object,
    isSingleMode: Boolean,
  },

  data() {
    return {
      responses: Object,
      query: this.$route.query,
      errMsg: '',
      isLoading: true,
      prqData: GroupQuestion,
      fetchedData: [],
      allAnswerList: {},
    };
  },

  created() {
    let params = { ...this.query };
    params.status = params.status || this.projectStatus;
    params.project_id = params.project_id || this.projectId;

    this.$store.dispatch('fulfill_integrate/get', params).then(() => {
      console.log('Success!');
      this.fetchedData = this.$store.state.fulfill_integrate.integrateData;

      this.fetchedData =
        this.fetchedData.sort(
          (a, b) => a.sorting_tag.localeCompare(b.sorting_tag, 'en', { numeric: true })
        );
      this.isLoading = false;
    }).catch((error) => {
      console.log(error.message);
      this.$notify({
        group: 'notice-stay',
        type: 'error',
        text: error.message,
      });
    });
  },

  computed: {
    isPrq() {
      return this.query.type === 'prq';
    },

    isAttachment() {
      return this.query.type === 'attachment';
    },

    saveChangesButton() {
      return this.fulfillSyncDetails?.pages?.portal_steps?.save_changes_button;
    },
  },

  methods: {
    saveAnswer(step) {
      const project_id = this.$route.params.id;
      this.isLoading = true;
      this.$store.dispatch('fulfill_integrate/get_project_selected', { project_id })
        .then((response) => {
          if(this.locked(response.project)) {
            this.$notify({
              group: 'notice-stay',
              type: 'error',
              text: 'This project is locked in the portal.',
            });
            this.$router.push('/applications?errorPersist=true');
          } else {
            if (step === 'saveChanges') {
              this.postData(step);
              return;
            }

            this.$refs.prqSave.validate()
              .then((isValid) => {
                if (isValid) {
                  if (this.query.type === 'attachment') {
                    this.isLoading = true;

                    this.isLoading = false;

                    if (step === 'nextStep') {
                      this.navigate();
                    } else if (step === 'linkStep') {
                      this.$router.push(this.stepsResponse.add_btn_url);
                    }

                    return;
                  }

                  this.postData(step);
                } else {
                  console.log('Not Validated.');
                  this.isLoading = false;

                  this.$notify({
                    group: 'notice-stay',
                    type: 'error',
                    text: this.fulfillSyncDetails.messages.validations,
                  });
                  this.$nextTick(() => {
                    const hasError = this.$el.querySelector('.has-error');
                    if (hasError) {
                      hasError.scrollIntoView({ block: 'center', behavior: 'smooth' });
                    }
                  });
                }
              })
          }
        })
        .catch((error) => {
          this.isLoading = false;
          console.log(error);
        })
    },

    addToAnswerList(groupId, answerObject) {
      if(this.allAnswerList[groupId] == null){
        this.allAnswerList[groupId] = answerObject;
      } else {
        this.allAnswerList[groupId] = { ...this.allAnswerList[groupId], ...answerObject };
      }
    },

    postData(step){
      this.isLoading = true;

      Object.keys(this.allAnswerList).forEach((groupId) => {
        const group = this.allAnswerList[groupId];
        Object.keys(group).forEach((questionId) => {
          const question = group[questionId];
          if (question.type === 'Specific values (multi-select)' && Array.isArray(question.response)) {
            question.response = question.response.join(';'); // Join responses for multi-select
          }
        });
      });

      const data = {
        answerList: this.allAnswerList,
        details: { ...this.query, project_id: this.projectId }
      }

      this.$store.dispatch('fulfill_integrate/post', data, this).then((response) => {
        if (this.isSingleMode) {

          this.$store.dispatch('auth/signOut');
          this.$router.push({ name: 'home' });
          return;
        }

        if (step === 'nextStep') {
          this.navigate();
        } else if (step === 'linkStep') {
          this.redirectPath(this.stepsResponse.add_btn_url);
        } else if (step === 'saveChanges') {
          this.$notify({
            group: 'notice',
            text: response.message,
          });
          this.isLoading = false;
        }
      }).catch((error) => {
        console.log(error.message);
        this.isLoading = false;
        this.$notify({
          group: 'notice-stay',
          type: 'error',
          text: error.message,
        });
      })
    },
  },
};
</script>

<style lang="scss" scoped>
.loading-size {
  min-width: 70rem;
}
@media (max-width: 768px) {
  .loading-size {
    min-width: 10rem;
  }
}
</style>
